import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ChevronLeft, Save, LineChart } from 'lucide-react';
import useApiMethods from '../../lib/useApiMethods';

const PLATFORMS = [
  { value: 'google_ads',        label: 'Google Ads' },
  { value: 'meta_ads',          label: 'Meta / Facebook Ads' },
  { value: 'instagram_ads',     label: 'Instagram Ads' },
  { value: 'youtube_ads',       label: 'YouTube Ads' },
  { value: 'linkedin_ads',      label: 'LinkedIn Ads' },
  { value: 'justdial',          label: 'JustDial' },
  { value: 'practo',            label: 'Practo' },
  { value: 'sulekha',           label: 'Sulekha' },
  { value: 'google_business',   label: 'Google Business Profile' },
  { value: 'email_campaign',    label: 'Email Campaign' },
  { value: 'sms_campaign',      label: 'SMS Campaign' },
  { value: 'whatsapp_campaign', label: 'WhatsApp Campaign' },
  { value: 'influencer',        label: 'Influencer / Partnership' },
  { value: 'other_directory',   label: 'Other Directory' },
  { value: 'other',             label: 'Other' },
];

const EMPTY = {
  name: '', platform: 'google_ads', status: 'planned',
  start_date: new Date().toISOString().slice(0, 10), end_date: '',
  budget: '', spend: '',
  campaign_url: '', utm_source: '', utm_medium: '', utm_campaign: '',
  target_department: '', notes: '',
};

export default function NewDigitalMarketing() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;
  const { create: apiCreate, update: apiUpdate, fetch: apiFetch } = useApiMethods({ throwOnFail: true });
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(isEdit);
  const [error, setError] = useState(null);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        const res = await apiFetch(`engage/digital-marketing-campaigns/${id}/`);
        const d = res?.data || res?.data?.data;
        if (d) setForm({ ...EMPTY, ...d,
          start_date: d.start_date || '',
          end_date: d.end_date || '' });
      } catch (e) {
        setError(e?.response?.data?.message || 'Failed to load campaign');
      } finally { setLoading(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.start_date || saving) return;
    setSaving(true); setError(null);
    try {
      const payload = { ...form };
      if (!payload.end_date) delete payload.end_date;
      if (!payload.budget) payload.budget = 0;
      if (!payload.spend) payload.spend = 0;
      if (isEdit) {
        await apiUpdate(`/engage/digital-marketing-campaigns/${id}/`, payload);
        navigate(`/engage/digital-marketing/${id}`);
      } else {
        const resp = await apiCreate('/engage/digital-marketing-campaigns/', payload);
        const created = resp?.data || resp?.data?.data;
        navigate(created?.id ? `/engage/digital-marketing/${created.id}` : '/engage/digital-marketing');
      }
    } catch (err) {
      setError(err?.response?.data?.message || (err?.response?.data && JSON.stringify(err.response.data)) || 'Failed to save');
      setSaving(false);
    }
  };

  const backTo = isEdit ? `/engage/digital-marketing/${id}` : '/engage/digital-marketing';
  const INPUT = "w-full px-3 py-2 text-sm rounded-lg bg-gray-50 dark:bg-gray-900/40 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500";
  const L = ({ children }) => <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">{children}</label>;

  if (loading) return <div className="p-8 text-center text-gray-500 dark:text-gray-400">Loading…</div>;

  return (
    <div className="p-4 max-w-3xl mx-auto space-y-4">
      <button onClick={() => navigate(backTo)} className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800">
        <ChevronLeft className="w-4 h-4" /> Back
      </button>
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center text-white shadow-sm"><LineChart className="w-5 h-5" /></div>
        <div><h1 className="text-xl font-bold text-gray-900 dark:text-white">{isEdit ? 'Edit' : 'New'} Marketing Campaign</h1></div>
      </div>
      {error && <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-sm text-red-700 dark:text-red-300">{error}</div>}
      <form onSubmit={submit} className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-100 dark:border-gray-700 shadow-sm space-y-4">
        <div><L>Campaign name <span className="text-red-500">*</span></L><input required value={form.name} onChange={e => set('name', e.target.value)} className={INPUT} /></div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <L>Platform <span className="text-red-500">*</span></L>
            <select required value={form.platform} onChange={e => set('platform', e.target.value)} className={INPUT}>
              {PLATFORMS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
          </div>
          <div>
            <L>Status</L>
            <select value={form.status} onChange={e => set('status', e.target.value)} className={INPUT}>
              <option value="planned">Planned</option>
              <option value="active">Active</option>
              <option value="paused">Paused</option>
              <option value="ended">Ended</option>
            </select>
          </div>
          <div><L>Start date <span className="text-red-500">*</span></L><input required type="date" value={form.start_date} onChange={e => set('start_date', e.target.value)} className={INPUT} /></div>
          <div><L>End date</L><input type="date" value={form.end_date} onChange={e => set('end_date', e.target.value)} className={INPUT} /></div>
          <div><L>Budget (₹)</L><input type="number" value={form.budget} onChange={e => set('budget', e.target.value)} className={INPUT} /></div>
          <div><L>Spend (₹)</L><input type="number" value={form.spend} onChange={e => set('spend', e.target.value)} className={INPUT} /></div>
          <div><L>Target department</L><input value={form.target_department} onChange={e => set('target_department', e.target.value)} className={INPUT} /></div>
          <div><L>Campaign URL</L><input type="url" value={form.campaign_url} onChange={e => set('campaign_url', e.target.value)} className={INPUT} /></div>
          <div><L>UTM source</L><input value={form.utm_source} onChange={e => set('utm_source', e.target.value)} className={INPUT} /></div>
          <div><L>UTM medium</L><input value={form.utm_medium} onChange={e => set('utm_medium', e.target.value)} className={INPUT} /></div>
          <div><L>UTM campaign</L><input value={form.utm_campaign} onChange={e => set('utm_campaign', e.target.value)} className={INPUT} /></div>
        </div>
        <div><L>Notes</L><textarea rows={3} value={form.notes} onChange={e => set('notes', e.target.value)} className={INPUT} /></div>
        <div className="flex items-center justify-end gap-2 pt-2 border-t border-gray-100 dark:border-gray-700">
          <button type="button" onClick={() => navigate(backTo)} className="px-4 py-2 text-sm rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700">Cancel</button>
          <button type="submit" disabled={saving || !form.name.trim() || !form.start_date} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-primary text-white text-sm shadow-sm hover:bg-primary/90 disabled:opacity-50">
            <Save className="w-3.5 h-3.5" /> {saving ? 'Saving…' : (isEdit ? 'Save Changes' : 'Create Campaign')}
          </button>
        </div>
      </form>
    </div>
  );
}
